import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HEADER_DONT_SAVE, HEADER_RESET } from '@app/constants';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';

import { ProjectPreview, ProjectPurchaseInfo } from '@interfaces/app';
import { UserService } from '../user/user.service';
import { Utils } from '../utils/utils.service';
import { Coordinates, GpsV2Service } from '../gps-v2/gps-v2.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { AuthService } from '../auth/auth.service';

// Interfaces para la funcion purchaseParticipations
interface PurchaseParams {
   amount: number;
   pid: string;
   otp: string;
   mfa?: boolean;
 }
export interface PurchaseResponse {
   status: string;
 }


@Injectable({
   providedIn: 'root'
})
export class ProjectsService {

   private readonly HEADER_TK = 'tk';
   private readonly HEADER_PLATFORM = 'monific-platform';
   private readonly HEADER_APP_VERSION = 'monific-app-version';

   private cache: { [prop: string]: Record<string, unknown> } = {};

   constructor(
      private readonly http: HttpClient,
      private readonly gpsServ: GpsV2Service,
      private readonly userServ: UserService,
      private readonly utils: Utils,
      private readonly analyticsServ: AnalyticsService,
      private readonly authServ: AuthService,
   ) { }

   getProjectsPreview(status: 'active' | 'funded' | 'liquidated') {
      return this.http.get<ProjectPreview[]>(`${environment.apiUrlBase}/project/prev/${status}`);
   }

   /** Obtener información necesaria para comprar participaciones */
   getProjectInfoToPurchase(pid: string): Observable<ProjectPurchaseInfo> {
      return this.http.get<ProjectPurchaseInfo>(`${environment.apiUrlBase}/project/info-purchase/${pid}`, this.cache.infoToPurchase || HEADER_RESET)
         .pipe(tap(() => this.cache.infoToPurchase = {}));
   }

   getInfoProjectV2(pid: string) {
      return this.http.get<any>(`${environment.apiUrlBase}/project/info-v2/${pid}`);
   }

   getUserParticipationsAmount() {
      return this.http.get<{ TOTAL_USER_PARTS: number, FIRST_PURCHASE_DATE?: string }>(`${environment.apiUrlBase}/project/user-participations`, HEADER_RESET);
   }

   // Refactorizacion de la funcion purchaseParticipations()
   private buildHeaders(otp: string): HttpHeaders {
      return HEADER_DONT_SAVE.headers
        .set(this.HEADER_TK, this.utils.encodeBase64(otp))
        .append(this.HEADER_PLATFORM, this.analyticsServ.getPlatformType())
        .append(this.HEADER_APP_VERSION, this.analyticsServ.getAppVersion());
   }
   private buildParams(pid: string, amount: number, location: string): Record<string, string> {
      const encodedCtd = this.utils.encodeBase64(`${pid}|${amount}`);
      return { ctd: encodedCtd, lct: location };
   }
   private encodeLocation(longitude: number, latitude: number, accuracy: number): string {
      return this.utils.encodeBase64(`${longitude}|${latitude}|${accuracy}`);
   }

   purchaseParticipations({amount,pid,otp, mfa = false}: PurchaseParams): Observable<PurchaseResponse> {
      return this.gpsServ.getOrRequestCoordinates().pipe(
        map(({ longitude, latitude, accuracy }) => {
          const referralCode = this.authServ.getReferralId();
          const lct = this.utils.encodeBase64(`${longitude}|${latitude}|${accuracy}`);
          const ctd = this.utils.encodeBase64(`${pid}|${amount}`);
          const headers = HEADER_DONT_SAVE.headers.set('tk', this.utils.encodeBase64(otp));
          const url = `${environment.apiUrlBase}${mfa ? '/project/mfa/purchase' : '/project/purchase'}`;
          return { url, params: { ctd, lct, intrff: referralCode }, headers };
        }),
        switchMap(({ url, params, headers }) =>
          this.http.post<any>(url, params, { headers }).pipe(
            map((res) => {
              this.userServ.cleanUserCache();
              this.cleanCache();
              return res;
            })
          )
        ),
        catchError((err) => {
          console.error('Error al realizar compra de participaciones:', err);
          return throwError(() => new Error('No se pudo completar la compra.'));
        })
      );
   }

  purchasePartsByBiometric(amount: number, pid: string): Observable<any> {
    return this.gpsServ.getOrRequestCoordinates().pipe(
      switchMap(coords => {
        if (!coords) { return throwError(() => new Error('Ubicación no disponible')); }

        const { longitude, latitude, accuracy } = coords;
        const location = this.utils.encodeBase64(`${longitude}|${latitude}|${accuracy}`);
        const params = {
          ctd: this.utils.encodeBase64(`${pid}|${amount}`),
          lct: location
        };
        const headers = HEADER_DONT_SAVE.headers;

        return this.http.post<any>(`${environment.apiUrlBase}/project/biometric/purchase`,params,{ headers }).pipe(map((r) => {
            this.userServ.cleanUserCache();
            this.cleanCache();
            return r;
          })
        );
      }),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

   getCommission(amount: number, partValue: number) {
      const num = (amount * partValue) * 0.0215;

      return Math.round(num * 100) / 100;
   }

   getCommissionIVA(commission: number) {
      const iva = commission * 0.16;

      return Math.round(iva * 100) / 100;
   }

   /** Obtener el rendimiento de un año por mes */
   getProjectPerformanceTarget(pid: string, title: string) {
      return this.http.get<any>(`${environment.apiUrlBase}/project/performance/${pid}/${title}`);
   }

   /** Obtener el status actual si estan activado los mails/SMS o MoNIP */
   getStatusMovements() {
      return this.http.get<{ info: any }>(`${environment.apiUrlBase}/public/get-status-function-automated`, { });
   }

   cleanCache() { this.cache = {} }
}
